import { promptInApp } from "../common/AppDialog";
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../../context/WorkspaceContext";
import {
  toSortedArray,
  getTopLevelTexts,
  getChildren,
  moveTextUnderParent,
  renameDesk,
  renameDrawer,
  renameNotebook,
  updateText,
  moveText,
  reorderTexts,
  createText,
  createFolderNote,
  addChecklistItem,
  setTextKind,
  applyReadingAction,
} from "../../services/workspaceService";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  ChevronRight,
  ChevronDown,
  Briefcase,
  BookOpen,
  FileText,
  FolderOpen,
  Search,
  X,
  Plus,
  FolderPlus,
} from "lucide-react";
import Usable from "../common/Usable";
import { useEditLock } from "../../context/EditLockContext";
import { getRememberedWritingText, rememberWritingText } from "../../utils/writingSession";
import { navigateToText } from "../../utils/textRoutes";
import { READING } from "../../config/readingStates";

function isHiddenFromBinder(text) {
  const s = text?.readingState;
  return s === READING.ARCHIVED || s === READING.TRASH;
}

async function promptTitle(label) {
  const title = await promptInApp({ title: label, confirmLabel: "Crear" });
  return title?.trim() || null;
}

export default function WorkspaceTree({ collapsed, onToggleCollapse }) {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const { canEdit } = useEditLock();
  const locked = !canEdit;
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [activeTextId, setActiveTextId] = useState(() => getRememberedWritingText()?.id || null);
  const filterRef = useRef(null);

  const hasExpandedRef = useRef(false);
  // Auto-expand desks on first load
  useEffect(() => {
    if (workspace?.desks?.length && !hasExpandedRef.current) {
      hasExpandedRef.current = true;
      const init = {};
      workspace.desks.forEach((d) => {
        init[`desk-${d.id}`] = true;
      });
      setExpanded(init);
    }
  }, [workspace?.desks]);

  // Keyboard shortcut: Ctrl/Cmd+Shift+B to focus tree filter
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === "B") {
        e.preventDefault();
        onToggleCollapse(false);
        setTimeout(() => filterRef.current?.focus(), 100);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onToggleCollapse]);

  useEffect(() => {
    const sync = () => setActiveTextId(getRememberedWritingText()?.id || null);
    sync();
    window.addEventListener("focus", sync);
    window.addEventListener("trufapp:writing-text", sync);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("trufapp:writing-text", sync);
    };
  }, []);

  const toggle = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOpenText = (text, desk, drawer, notebook) => {
    if (!text) return;
    const enriched = {
      ...text,
      deskId: desk?.id || text.deskId,
      drawerId: drawer?.id || text.drawerId,
      notebookId: notebook?.id || text.notebookId,
      deskSlug: desk?.slug || text.deskSlug,
      drawerSlug: drawer?.slug || text.drawerSlug,
      notebookSlug: notebook?.slug || text.notebookSlug,
    };
    rememberWritingText(enriched);
    setActiveTextId(enriched.id);
    navigateToText(navigate, enriched);
  };

  const handleCreateNote = async (desk, drawer, notebook, parentId = null) => {
    if (locked) return;
    const title = await promptTitle(parentId ? "Nueva nota en carpeta" : "Nueva nota");
    if (!title) return;
    try {
      let id;
      if (parentId) {
        id = await addChecklistItem(desk.id, drawer.id, notebook.id, parentId, title);
        setExpanded((prev) => ({ ...prev, [`folder-${parentId}`]: true, [`notebook-${notebook.id}`]: true }));
      } else {
        id = await createText(desk.id, drawer.id, notebook.id, title, "");
        setExpanded((prev) => ({ ...prev, [`notebook-${notebook.id}`]: true }));
      }
      if (id) {
        handleOpenText(
          { id, title, kind: "note", parentId: parentId || undefined },
          desk,
          drawer,
          notebook,
        );
      }
    } catch (e) {
      console.error("[WorkspaceTree] create note failed:", e);
    }
  };

  const handleCreateFolder = async (desk, drawer, notebook, parentId = null) => {
    if (locked) return;
    const title = await promptTitle(parentId ? "Nueva subcarpeta" : "Nueva carpeta");
    if (!title) return;
    try {
      let id;
      if (parentId) {
        id = await addChecklistItem(desk.id, drawer.id, notebook.id, parentId, title, { asFolder: true });
        setExpanded((prev) => ({ ...prev, [`folder-${parentId}`]: true, [`notebook-${notebook.id}`]: true }));
      } else {
        id = await createFolderNote(desk.id, drawer.id, notebook.id, title);
        setExpanded((prev) => ({ ...prev, [`notebook-${notebook.id}`]: true }));
      }
      if (id) {
        handleOpenText(
          { id, title, kind: "folder", parentId: parentId || undefined },
          desk,
          drawer,
          notebook,
        );
      }
    } catch (e) {
      console.error("[WorkspaceTree] create folder failed:", e);
    }
  };

  const handleConvertKind = async (desk, drawer, notebook, text) => {
    if (locked) return;
    const next = text.kind === "folder" ? "note" : "folder";
    try {
      await setTextKind(desk.id, drawer.id, notebook.id, text.id, next);
    } catch (e) {
      console.error("[WorkspaceTree] convert kind failed:", e);
    }
  };

  const handleArchive = async (desk, drawer, notebook, text) => {
    if (locked) return;
    const archived = text.readingState === READING.ARCHIVED;
    try {
      await applyReadingAction(
        desk.id,
        drawer.id,
        notebook.id,
        text.id,
        archived ? "restore" : "archive",
      );
    } catch (e) {
      console.error("[WorkspaceTree] archive failed:", e);
    }
  };

  const textMenuItems = (desk, drawer, notebook, text) => {
    if (locked) return [];
    const isFolder = text.kind === "folder";
    const archived = text.readingState === READING.ARCHIVED;
    const items = [
      {
        label: isFolder ? "Convertir en nota" : "Convertir en carpeta",
        onClick: () => handleConvertKind(desk, drawer, notebook, text),
      },
      {
        label: archived ? "Restaurar del archivo" : "Archivar",
        onClick: () => handleArchive(desk, drawer, notebook, text),
        danger: !archived,
      },
    ];
    if (isFolder) {
      items.unshift(
        { label: "Nueva nota aquí", onClick: () => handleCreateNote(desk, drawer, notebook, text.id) },
        { label: "Nueva subcarpeta", onClick: () => handleCreateFolder(desk, drawer, notebook, text.id) },
      );
    }
    return items;
  };

  const findNotebookPath = useCallback(
    (notebookId) => {
      if (!workspace?.desks?.length) return null;
      for (const desk of workspace.desks) {
        for (const drawer of toSortedArray(desk.drawers)) {
          for (const notebook of toSortedArray(drawer.notebooks)) {
            if (notebook.id === notebookId) {
              return { deskId: desk.id, drawerId: drawer.id, notebookId: notebook.id };
            }
          }
        }
      }
      return null;
    },
    [workspace],
  );

  const getTextIdsInNotebook = useCallback(
    (notebookId) => {
      if (!workspace?.desks?.length) return [];
      for (const desk of workspace.desks) {
        for (const drawer of toSortedArray(desk.drawers)) {
          for (const notebook of toSortedArray(drawer.notebooks)) {
            if (notebook.id === notebookId) {
              const top = getTopLevelTexts(toSortedArray(notebook.texts));
              const visible = top.filter((t) => showArchived || !isHiddenFromBinder(t)).map((t) => t.id);
              // ponytail: archived keep relative order after visible (DnD only among shown)
              const hidden = top.filter((t) => isHiddenFromBinder(t)).map((t) => t.id);
              return showArchived ? top.map((t) => t.id) : [...visible, ...hidden];
            }
          }
        }
      }
      return [];
    },
    [workspace, showArchived],
  );

  const handleTreeDragEnd = useCallback(
    async (result) => {
      try {
        const { destination, source, draggableId } = result || {};
        if (!destination) return;
        if (locked) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const textId = String(draggableId).replace(/^t:/, "");
        if (!textId) return;

        // Drop onto a folder note: nest under it
        if (String(destination.droppableId).startsWith("folder:")) {
          const parentId = String(destination.droppableId).replace(/^folder:/, "");
          const destNotebookId = String(source.droppableId).replace(/^nb:/, "").replace(/^folder:/, "");
          // Find notebook of parent
          let path = null;
          let parentNotebookId = null;
          for (const desk of workspace?.desks || []) {
            for (const drawer of toSortedArray(desk.drawers)) {
              for (const notebook of toSortedArray(drawer.notebooks)) {
                const texts = toSortedArray(notebook.texts);
                if (texts.some((t) => t.id === parentId)) {
                  path = { deskId: desk.id, drawerId: drawer.id, notebookId: notebook.id };
                  parentNotebookId = notebook.id;
                }
              }
            }
          }
          if (!path) return;
          const srcPath = findNotebookPath(String(source.droppableId).replace(/^nb:/, "")) || path;
          if (srcPath.notebookId !== path.notebookId) {
            await moveText(
              srcPath.deskId,
              srcPath.drawerId,
              srcPath.notebookId,
              textId,
              path.deskId,
              path.drawerId,
              path.notebookId,
            );
          }
          await moveTextUnderParent(path.deskId, path.drawerId, path.notebookId, textId, parentId);
          return;
        }

        const srcNotebookId = String(source.droppableId).replace(/^nb:/, "");
        const destNotebookId = String(destination.droppableId).replace(/^nb:/, "");
        if (!srcNotebookId || !destNotebookId) return;

        const srcPath = findNotebookPath(srcNotebookId);
        const destPath = findNotebookPath(destNotebookId);
        if (!srcPath || !destPath) return;

        const srcIds = getTextIdsInNotebook(srcNotebookId);
        const destIdsBase = getTextIdsInNotebook(destNotebookId);

        // Promote to top-level when dropping on notebook list
        await moveTextUnderParent(srcPath.deskId, srcPath.drawerId, srcPath.notebookId, textId, null);

        if (srcNotebookId === destNotebookId) {
          const next = [...srcIds];
          const fromIdx = next.indexOf(textId);
          if (fromIdx >= 0) next.splice(fromIdx, 1);
          next.splice(destination.index, 0, textId);
          await reorderTexts(srcPath.deskId, srcPath.drawerId, srcPath.notebookId, next);
          return;
        }

        await moveText(
          srcPath.deskId,
          srcPath.drawerId,
          srcPath.notebookId,
          textId,
          destPath.deskId,
          destPath.drawerId,
          destPath.notebookId,
        );

        const nextSrc = srcIds.filter((id) => id !== textId);
        const nextDest = destIdsBase.filter((id) => id !== textId);
        nextDest.splice(destination.index, 0, textId);

        await reorderTexts(srcPath.deskId, srcPath.drawerId, srcPath.notebookId, nextSrc);
        await reorderTexts(destPath.deskId, destPath.drawerId, destPath.notebookId, nextDest);
      } catch (e) {
        console.error("[WorkspaceTree] DnD failed:", e);
      }
    },
    [locked, findNotebookPath, getTextIdsInNotebook, workspace],
  );

  // Swipe-to-dismiss for mobile sidebar
  const touchStartX = useRef(null);
  const onTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const onTouchEnd = useCallback((e) => {
    if (touchStartX.current == null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (deltaX < -60) {
      onToggleCollapse(true);
    }
    touchStartX.current = null;
  }, [onToggleCollapse]);

  const desks = useMemo(() => {
    if (!workspace?.desks) return [];
    return workspace.desks;
  }, [workspace]);

  const filteredDesks = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return desks
      .map((desk) => {
        const drawers = toSortedArray(desk.drawers)
          .map((drawer) => {
            const notebooks = toSortedArray(drawer.notebooks)
              .map((notebook) => {
                let texts = toSortedArray(notebook.texts);
                if (!showArchived) {
                  texts = texts.filter((t) => !isHiddenFromBinder(t));
                }
                if (q) {
                  texts = texts.filter(
                    (t) =>
                      (t.title || "").toLowerCase().includes(q) ||
                      (t.content || "").toLowerCase().includes(q)
                  );
                  if (texts.length === 0 && !(notebook.name || "").toLowerCase().includes(q)) {
                    return null;
                  }
                }
                return { ...notebook, texts };
              })
              .filter(Boolean);
            if (notebooks.length === 0 && q && !(drawer.name || "").toLowerCase().includes(q)) {
              return null;
            }
            return { ...drawer, notebooks };
          })
          .filter(Boolean);
        if (drawers.length === 0 && q && !(desk.name || "").toLowerCase().includes(q)) {
          return null;
        }
        return { ...desk, drawers };
      })
      .filter(Boolean);
  }, [desks, filter, showArchived]);

  const isDeskOpen = (id) => !!expanded[`desk-${id}`];
  const isDrawerOpen = (id) => !!expanded[`drawer-${id}`];
  const isNotebookOpen = (id) => !!expanded[`notebook-${id}`];

  const countTexts = (notebook) =>
    toSortedArray(notebook.texts).filter((t) => showArchived || !isHiddenFromBinder(t)).length;

  const renderTextNode = (text, desk, drawer, notebook, idx, depth) => {
    const isFolder = text.kind === "folder";
    const kids = isFolder
      ? getChildren(toSortedArray(notebook.texts), text.id).filter(
          (t) => showArchived || !isHiddenFromBinder(t),
        )
      : [];
    const folderKey = `folder-${text.id}`;
    const folderExpanded = !!expanded[folderKey];
    const isActive = activeTextId === text.id;
    const archived = text.readingState === READING.ARCHIVED;

    return (
      <div key={text.id}>
        <Draggable
          draggableId={`t:${text.id}`}
          index={idx}
          isDragDisabled={locked}
        >
          {(dragProvided, snapshot) => (
            <div
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              style={{
                ...(dragProvided.draggableProps.style || {}),
                opacity: snapshot.isDragging ? 0.96 : archived ? 0.45 : 1,
                transform: snapshot.isDragging
                  ? `${dragProvided.draggableProps.style?.transform || ""} rotate(-0.7deg)`
                  : dragProvided.draggableProps.style?.transform,
                transition: snapshot.isDropAnimating
                  ? "transform 180ms cubic-bezier(0.2, 0, 0, 1), opacity 180ms ease"
                  : dragProvided.draggableProps.style?.transition,
                boxShadow: snapshot.isDragging ? "0 10px 24px rgba(0,0,0,0.35)" : "none",
                borderRadius: 6,
              }}
            >
              <Usable
                location={[desk.name, drawer.name, notebook.name, text.title || "Sin título"]}
                title={text.title || "Sin título"}
                onRename={locked ? undefined : (name) => updateText(desk.id, drawer.id, notebook.id, text.id, { title: name })}
                copyLabel="Copiar título"
                copyValue={text.title || "Sin título"}
                onClick={() => {
                  if (isFolder) toggle(folderKey);
                  handleOpenText(text, desk, drawer, notebook);
                }}
                dragHandleProps={dragProvided.dragHandleProps}
                menuItems={textMenuItems(desk, drawer, notebook, text)}
                style={rowStyle(3 + depth, isActive)}
                className="tree-row"
              >
                {isFolder ? (
                  folderExpanded ? <ChevronDown size={11} color="#D4AF37" /> : <ChevronRight size={11} color="#D4AF37" />
                ) : null}
                {isFolder ? (
                  <FolderOpen size={11} color="#D4AF37" />
                ) : text.img ? (
                  <img
                    src={text.img}
                    alt=""
                    width={14}
                    height={14}
                    style={{ borderRadius: 3, objectFit: "cover", flexShrink: 0 }}
                  />
                ) : (
                  <FileText size={11} color="#888" />
                )}
                <span style={{ color: text.done ? "#666" : isActive ? "#F9F6EE" : "#A89F91", overflow: "hidden", textOverflow: "ellipsis", textDecoration: text.done ? "line-through" : "none" }}>
                  {text.title || "Sin título"}
                </span>
                {isFolder && kids.length > 0 && (
                  <span className="tree-row-badge">{kids.length}</span>
                )}
              </Usable>
            </div>
          )}
        </Draggable>
        {isFolder && folderExpanded && (
          <Droppable droppableId={`folder:${text.id}`} type="text">
            {(folderDrop, folderSnap) => (
              <div
                ref={folderDrop.innerRef}
                {...folderDrop.droppableProps}
                className="tree-nest"
                style={{
                  minHeight: 8,
                  background: folderSnap.isDraggingOver ? "rgba(212,175,55,0.08)" : "transparent",
                  borderRadius: 6,
                }}
              >
                {kids.map((child, cIdx) => renderTextNode(child, desk, drawer, notebook, cIdx, depth + 1))}
                {folderDrop.placeholder}
              </div>
            )}
          </Droppable>
        )}
      </div>
    );
  };
  const countNotebooks = (drawer) => toSortedArray(drawer.notebooks).length;
  const countDrawers = (desk) => toSortedArray(desk.drawers).length;

  const rowStyle = (depth = 0, isActive = false) => ({
    paddingLeft: 4 + depth * 10,
    cursor: "pointer",
    color: isActive ? "#F9F6EE" : "#A89F91",
    background: isActive ? "rgba(212,175,55,0.15)" : "transparent",
  });

  const treeCreateBtn = {
    background: "none",
    border: "none",
    color: "#666",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    borderRadius: 4,
  };

  if (collapsed) {
    return (
      <div
        style={{
          width: "36px",
          minWidth: "36px",
          borderRight: "1px solid rgba(212,175,55,0.1)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: "12px",
          background: "rgba(42, 27, 20, 0.6)",
        }}
      >
        <button
          onClick={() => onToggleCollapse(false)}
          title="Mostrar árbol (Ctrl+Shift+B)"
          style={{
            background: "none",
            border: "none",
            color: "#D4AF37",
            cursor: "pointer",
            padding: "6px",
            borderRadius: "4px",
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleTreeDragEnd}>
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        width: "260px",
        minWidth: "260px",
        maxWidth: "260px",
        borderRight: "1px solid rgba(212,175,55,0.1)",
        background: "rgba(42, 27, 20, 0.85)",
        backdropFilter: "blur(8px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "6px 8px",
          borderBottom: "1px solid rgba(212,175,55,0.1)",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          minHeight: 44,
          boxSizing: "border-box",
        }}
      >
        <span style={{ color: "#D4AF37", fontSize: "0.7rem", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase", flex: 1 }}>
          Explorador
        </span>
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          title={showArchived ? "Ocultar archivo" : "Mostrar archivo"}
          style={{
            background: showArchived ? "rgba(212,175,55,0.2)" : "none",
            border: "none",
            color: showArchived ? "#D4AF37" : "#666",
            cursor: "pointer",
            padding: "2px 4px",
            fontSize: "0.65rem",
            borderRadius: 4,
          }}
        >
          Archivo
        </button>
        <button
          onClick={() => onToggleCollapse(true)}
          title="Ocultar"
          style={{ background: "none", border: "none", color: "#D4AF37", cursor: "pointer", padding: "2px" }}
        >
          <X size={14} />
        </button>
      </div>

      {/* Filter */}
      <div style={{ padding: "4px 6px", borderBottom: "1px solid rgba(212,175,55,0.05)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", background: "rgba(0,0,0,0.3)", borderRadius: "4px", padding: "2px 6px", minHeight: 32, boxSizing: "border-box" }}>
          <Search size={12} color="#D4AF37" style={{ opacity: 0.5 }} />
          <input
            ref={filterRef}
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrar..."
            style={{ background: "transparent", border: "none", color: "#D4AF37", fontSize: "0.75rem", outline: "none", width: "100%", fontFamily: "serif" }}
          />
        </div>
      </div>

      {/* Tree */}
      <div className="workspace-tree-scroll">
        {filteredDesks.map((desk) => (
          <div key={desk.id} className="tree-nest">
            <Usable
              location={[desk.name]}
              title={desk.name}
              onRename={locked ? undefined : (name) => renameDesk(desk.id, name)}
              copyValue={desk.name}
              onClick={() => toggle(`desk-${desk.id}`)}
              style={rowStyle(0)}
              className="tree-row"
            >
              {isDeskOpen(desk.id) ? <ChevronDown size={12} color="#D4AF37" /> : <ChevronRight size={12} color="#D4AF37" />}
              <Briefcase size={12} color="#D4AF37" />
              <span style={{ fontWeight: "bold", color: "#D4AF37", overflow: "hidden", textOverflow: "ellipsis" }}>{desk.name}</span>
              <span className="tree-row-badge">{countDrawers(desk)}</span>
            </Usable>

            {isDeskOpen(desk.id) && (
              <div className="tree-nest">
                {toSortedArray(desk.drawers).map((drawer) => (
                  <div key={drawer.id} className="tree-nest">
                    <Usable
                      location={[desk.name, drawer.name]}
                      title={drawer.name}
                              onRename={locked ? undefined : (name) => renameDrawer(desk.id, drawer.id, name)}
                      copyValue={`${desk.name} › ${drawer.name}`}
                      onClick={() => toggle(`drawer-${drawer.id}`)}
                      style={rowStyle(1)}
                      className="tree-row"
                    >
                      {isDrawerOpen(drawer.id) ? <ChevronDown size={11} color="#A89F91" /> : <ChevronRight size={11} color="#A89F91" />}
                      <FolderOpen size={11} color="#A89F91" />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{drawer.name}</span>
                      <span className="tree-row-badge">{countNotebooks(drawer)}</span>
                    </Usable>

                    {isDrawerOpen(drawer.id) && (
                      <div className="tree-nest">
                        {toSortedArray(drawer.notebooks).map((notebook) => (
                          <div key={notebook.id} className="tree-nest">
                            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                              <Usable
                                location={[desk.name, drawer.name, notebook.name]}
                                title={notebook.name}
                                onRename={locked ? undefined : (name) => renameNotebook(desk.id, drawer.id, notebook.id, name)}
                                copyValue={`${desk.name} › ${drawer.name} › ${notebook.name}`}
                                onClick={() => toggle(`notebook-${notebook.id}`)}
                                style={{ ...rowStyle(2), flex: 1, minWidth: 0 }}
                                className="tree-row"
                                menuItems={
                                  locked
                                    ? []
                                    : [
                                        {
                                          label: "Nueva nota",
                                          onClick: () => handleCreateNote(desk, drawer, notebook),
                                        },
                                        {
                                          label: "Nueva carpeta",
                                          onClick: () => handleCreateFolder(desk, drawer, notebook),
                                        },
                                      ]
                                }
                              >
                                {isNotebookOpen(notebook.id) ? <ChevronDown size={11} color="#A89F91" /> : <ChevronRight size={11} color="#A89F91" />}
                                <BookOpen size={11} color={notebook.color || "#A89F91"} />
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{notebook.name}</span>
                                <span className="tree-row-badge">{countTexts(notebook)}</span>
                              </Usable>
                              {!locked && (
                                <div style={{ display: "flex", flexShrink: 0, paddingRight: 2 }}>
                                  <button
                                    type="button"
                                    className="tree-create-btn"
                                    title="Nueva nota"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCreateNote(desk, drawer, notebook);
                                    }}
                                    style={treeCreateBtn}
                                  >
                                    <Plus size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    className="tree-create-btn"
                                    title="Nueva carpeta"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCreateFolder(desk, drawer, notebook);
                                    }}
                                    style={treeCreateBtn}
                                  >
                                    <FolderPlus size={12} />
                                  </button>
                                </div>
                              )}
                            </div>

                            {isNotebookOpen(notebook.id) && (
                              <div className="tree-nest">
                                <Droppable droppableId={`nb:${notebook.id}`} type="text">
                                  {(dropProvided, dropSnapshot) => (
                                    <div
                                      ref={dropProvided.innerRef}
                                      {...dropProvided.droppableProps}
                                      style={{
                                        minHeight: 10,
                                        borderRadius: 6,
                                        background: dropSnapshot.isDraggingOver ? "rgba(212,175,55,0.08)" : "transparent",
                                        transition: "background 140ms ease",
                                      }}
                                    >
                                      {getTopLevelTexts(toSortedArray(notebook.texts))
                                        .filter((t) => showArchived || !isHiddenFromBinder(t))
                                        .map((text, idx) =>
                                          renderTextNode(text, desk, drawer, notebook, idx, 0)
                                        )}
                                      {dropProvided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {filteredDesks.length === 0 && (
          <div style={{ padding: "20px", textAlign: "center", color: "#666", fontSize: "0.75rem" }}>
            No se encontraron resultados.
          </div>
        )}
      </div>
    </div>
    </DragDropContext>
  );
}
